import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import './App.css';
import '../src/components/layout/layout.scss';
import "./pages/bulkImportTool/bulkImport.scss";
import "./pages/customerOrders/customerOrders.scss";
import NF404 from './components/common/404/404';
import "./components/common/commonStyles.scss";
import MainLayout from './components/layout/mainLayout';
import { loginRequest } from './configs/auth/authConfig';
import Dashboard from './pages/dashboard/dashboard';
import { Navigate, Route, Routes } from 'react-router';
import { useEffect, useRef, useState } from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { useUserAuth } from './context/UserContext';
import { Space, Spin, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import msalInstance from './configs/auth/msalInstance';
import { menuLinks } from './utils/constants';
import ImportLogs from './pages/bulkImportTool/importLogs/importLogs';
import CustomerOrders from './pages/customerOrders/ordersManagement/customerOrdersList';
import CustomerOrderDetails from './pages/customerOrders/ordersManagement/customerOrderDetails';
import RolesList from './pages/configurations/roles/rolesList';
import UsersList from './pages/configurations/users/usersList';
import FullScreenLoader from './components/common/fullScreenLoader';
import CreditNote from './pages/customerOrders/creditNote/creditNote';
import { hasPermission, permissionsMenu } from './utils/permissionUtils';
import Unauthorized from './components/common/unauthorized/unauthorized';
import NewSingelOrder from './pages/customerOrders/singelOrder/newSingelOrder';
import TemplateList from './pages/configurations/templates/templatesList';
import LocalFiles from './pages/configurations/localFiles/localFiles';
import NewOrderD2C from './pages/customerOrders/newOrderD2C/newD2COrder';
import CreditNoteDetails from './pages/customerOrders/creditNote/creditNoteDetails';

const { Text } = Typography;

interface ProtectedByRoleProps {
  permission: string;
  children: React.ReactNode;
}

const ProtectedByRole: React.FC<ProtectedByRoleProps> = ({ permission, children }) => {
  const { user, isUnauthorized } = useUserAuth();

  if (!permission) return <>{children}</>;
  if (isUnauthorized) return <Navigate to="/unauthorized" replace />;
  if (!user) return null;

  return hasPermission(user.roleCode, permission)
    ? <>{children}</>
    : <Navigate to="/" replace />;
};


function App() {
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { user, isLoadingUser, isUnauthorized, refreshUser } = useUserAuth();
  const [tookTooLong, setTookTooLong] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None || isLoadingUser) {
      if (!timeoutRef.current) {
        timeoutRef.current = setTimeout(() => setTookTooLong(true), 15000);
      }
    } else {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setTookTooLong(false);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [inProgress, isLoadingUser]);

  useEffect(() => {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0 && inProgress === InteractionStatus.None) {
      msalInstance.loginRedirect(loginRequest);
    }
  }, [msalInstance, inProgress]);

  const isMsalBusy = inProgress !== InteractionStatus.None;

  // Block entire app until MSAL is idle and user context is resolved
  if (isMsalBusy || !isAuthenticated || (isAuthenticated && isLoadingUser)) {
    return (
      <Space direction='vertical' style={{ display: "flex", textAlign: "center", marginTop: "50px" }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 42 }} spin />} fullscreen > </Spin>
        <Text>Loading...</Text>
        {tookTooLong && (
          <>
            <Text type="secondary">This is taking longer than expected.</Text>
            <Text>
              <a onClick={() => refreshUser()}>Retry now</a>
            </Text>
          </>
        )}
      </Space>
    )
  }

  if (!isMsalBusy && isAuthenticated && !isLoadingUser && isUnauthorized) {
    return <Navigate to="/unauthorized" replace />
  }


  return (
    <>
      <FullScreenLoader />
      <Routes>

        <Route path="/unauthorized" element={<Unauthorized />} />

        <Route path="/" element={<MainLayout />}>

          {/* Dashboard Routes */}
          <Route path={`${menuLinks.dashboard}`} element={<ProtectedByRole permission={permissionsMenu.MENU_DASHBOARD}> <Dashboard /></ProtectedByRole>} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* Bulk Import tool Routes */}
          <Route path={`${menuLinks.importLogs}`} element={<ProtectedByRole permission={permissionsMenu.MENU_LOGS}><ImportLogs /></ProtectedByRole>} />
          
          {/* Customers Orders Routes */}
          <Route path={`${menuLinks.customerOrders}`} element={<ProtectedByRole permission={permissionsMenu.MENU_ORDERS}><CustomerOrders /></ProtectedByRole>} />
          <Route path={`${menuLinks.customerOrderDetails + '/view/:id'}`} element={<ProtectedByRole permission={permissionsMenu.MENU_ORDERS}><CustomerOrderDetails /></ProtectedByRole>} />
          {/* <Route path={`${menuLinks.creditNote}`} element={<ProtectedByRole permission={permissionsMenu.MENU_CONFIGS}><CreditNote /></ProtectedByRole>} /> */}

          {/* Users Routes */}
          <Route path={`${menuLinks.users}`} element={<ProtectedByRole permission={permissionsMenu.MENU_USERS}><UsersList /></ProtectedByRole>} />

          {/* Roles Routes */}
          <Route path={`${menuLinks.roles}`} element={<ProtectedByRole permission={permissionsMenu.MENU_ROLES}> <RolesList /> </ProtectedByRole>} />

          {/* Singel order Routes */}
          <Route path={`${menuLinks.singelOrder}`} element={<ProtectedByRole permission={permissionsMenu.MENU_SINGEL_ORDER}> <NewSingelOrder /> </ProtectedByRole>} />

          {/* Templates Routes */}
          <Route path={`${menuLinks.templates}`} element={<ProtectedByRole permission={permissionsMenu.MENU_TEMPLATES}> <TemplateList /> </ProtectedByRole>} />

          {/* LocalFiles Routes */}
          <Route path={`${menuLinks.localFiles}`} element={<ProtectedByRole permission={permissionsMenu.MENU_LOCALFILES}> <LocalFiles /> </ProtectedByRole>} />

          {/* D2C OrderRoutes */}
          <Route path={`${menuLinks.d2cOrder}`} element={<ProtectedByRole permission={permissionsMenu.MENU_D2C_ORDER_CREATE}> <NewOrderD2C /> </ProtectedByRole>} />

          {/* Credit Note Routes */}
          <Route path={`${menuLinks.creditNote}`} element={<ProtectedByRole permission={permissionsMenu.MENU_CREDIT_NOTE}> <CreditNote /> </ProtectedByRole>} />
          <Route path={`${menuLinks.creditNoteDetails + '/view/:id'}`} element={<ProtectedByRole permission={permissionsMenu.MENU_CREDIT_NOTE}> <CreditNoteDetails /> </ProtectedByRole>} />

          <Route path="*" element={<NF404 />} />

        </Route>
      </Routes>
    </>
  );
}

export default App;
